import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button.js";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card.js";
import { Input } from "@/components/ui/input.js";
import { Label } from "@/components/ui/label.js";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs.js";
import { useToast } from "@/hooks/use-toast.js";
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updateProfile,
} from "firebase/auth";

interface AuthModalProps {
  onClose: () => void;
  initialTab?: "signin" | "signup";
}

const getFirebaseErrorMessage = (error: unknown): string => {
  // Log the full error to the console for more detailed debugging
  console.error("Firebase Auth Error:", error);

  if (error instanceof Error && "code" in error) {
    const errorCode = (error as { code: string }).code;
    switch (errorCode) {
      case "auth/invalid-credential":
      case "auth/wrong-password": // Older SDKs
      case "auth/user-not-found": // Older SDKs might use this for login failure
        return "Invalid email or password. Please try again.";
      case "auth/email-already-in-use":
        return "This email is already registered. Please sign in.";
      case "auth/weak-password":
        return "Password is too weak. It should be at least 6 characters.";
      case "auth/user-disabled":
        return "This user account has been disabled by an administrator.";
      case "auth/too-many-requests":
        return "Access to this account has been temporarily disabled due to many failed login attempts. You can reset your password or try again later.";
      default:
        return "An unexpected error occurred during authentication. Please check the console for details.";
    }
  }
  return "An unknown error occurred during authentication.";
};

export const AuthModal = ({
  onClose,
  initialTab = "signin",
}: AuthModalProps) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    const formData = new FormData(e.currentTarget);
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;

    try {
      const auth = getAuth();
      await signInWithEmailAndPassword(auth, email, password);
      // On success, the onAuthStateChanged listener in Index.tsx will handle the user state.
      onClose();
      toast({
        title: "Welcome back!",
        description: "You've successfully logged in.",
      });
    } catch (error: unknown) {
      toast({
        title: "Login Failed",
        description: getFirebaseErrorMessage(error),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    const formData = new FormData(e.currentTarget);
    const name = formData.get("name") as string;
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;

    try {
      const auth = getAuth();
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      // After creating the user, set their display name
      await updateProfile(userCredential.user, { displayName: name });

      // The onAuthStateChanged listener in your app will now pick up the new user with their display name.
      onClose();
      toast({
        title: "Account created!",
        description: "Welcome! You're now logged in.",
      });
    } catch (error: unknown) {
      toast({
        title: "Signup Failed",
        description: getFirebaseErrorMessage(error),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-md bg-[#B7B7B7] border-slate-700 text-slate-200">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl text-slate-900">
            Welcome to Startup Marquee
          </CardTitle>
          <CardDescription className="text-slate-700">
            Sign in below or Create an account if you&apos;re new!
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs
            value={activeTab}
            onValueChange={(value) =>
              setActiveTab(value as "signin" | "signup")
            }
            className="w-full"
          >
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger
                value="signin"
                className="data-[state=active]:bg-slate-800 data-[state=active]:text-slate-100"
              >
                Sign In
              </TabsTrigger>
              <TabsTrigger
                value="signup"
                className="data-[state=active]:bg-slate-800 data-[state=active]:text-slate-100"
              >
                Sign Up
              </TabsTrigger>
            </TabsList>

            <TabsContent value="signin">
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="signin-email" className="text-slate-700">
                    Email
                  </Label>
                  <Input
                    id="signin-email"
                    name="email"
                    type="email"
                    placeholder="john@example.com"
                    required
                    className="bg-slate-100 border-slate-600 text-slate-700"
                  />
                </div>
                <div>
                  <Label htmlFor="signin-password" className="text-slate-700">
                    Password
                  </Label>
                  <Input
                    id="signin-password"
                    name="password"
                    type="password"
                    required
                    className="bg-slate-100 border-slate-600 text-slate-700"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    type="submit"
                    className="flex-1 bg-slate-700 hover:bg-slate-800"
                    disabled={isLoading}
                  >
                    {isLoading ? "Signing In..." : "Sign In"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onClose}
                    className="text-slate-700 border-slate-600 hover:bg-slate-800 hover:text-slate-100"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </TabsContent>

            <TabsContent value="signup">
              <form onSubmit={handleSignup} className="space-y-4">
                <div>
                  <Label htmlFor="signup-name" className="text-slate-700">
                    Full Name
                  </Label>
                  <Input
                    id="signup-name"
                    name="name"
                    placeholder="John Doe"
                    required
                    className="bg-slate-100 border-slate-600 text-slate-700"
                  />
                </div>
                <div>
                  <Label htmlFor="signup-email" className="text-slate-700">
                    Email
                  </Label>
                  <Input
                    id="signup-email"
                    name="email"
                    type="email"
                    placeholder="john@example.com"
                    required
                    className="bg-slate-100 border-slate-600 text-slate-700"
                  />
                </div>
                <div>
                  <Label htmlFor="signup-password" className="text-slate-700">
                    Password
                  </Label>
                  <Input
                    id="signup-password"
                    name="password"
                    type="password"
                    required
                    className="bg-slate-100 border-slate-600 text-slate-700"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    type="submit"
                    className="flex-1 bg-slate-700 hover:bg-slate-800"
                    disabled={isLoading}
                  >
                    {isLoading ? "Creating Account..." : "Sign Up"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onClose}
                    className="text-slate-700 border-slate-600 hover:bg-slate-800 hover:text-slate-100"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};
